import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCatches, deleteCatch } from '../api/catches';
import { listClubs } from '../api/clubs';
import type { CatchResponse, ClubResponse } from '../types/api';
import { CatchesMap } from '../components/CatchesMap';
import { CatchTable } from '../components/catches/CatchTable';
import { fromCatchResponse } from '../components/catches/catchRow';

export function CatchesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CatchResponse[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'map'>('list');
  const [clubs, setClubs] = useState<ClubResponse[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const pageSize = 25;

  const selectedClub = selectedClubId != null ? clubs.find(c => c.id === selectedClubId) ?? null : null;

  async function load(p: number, clubId: number | null) {
    setLoading(true);
    setError(null);
    try {
      const res = await listCatches({ page: p, pageSize, clubId: clubId ?? undefined });
      setItems(res.items);
      setTotalCount(res.totalCount);
    } catch {
      setItems([]);
      setTotalCount(0);
      setError('Failed to load catches.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(page, selectedClubId); }, [page, selectedClubId]);

  useEffect(() => {
    listClubs().then(setClubs).catch(() => { /* non-blocking */ });
  }, []);

  function handleClubChange(value: string) {
    setPage(1);
    setSelectedClubId(value === '' ? null : Number(value));
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this catch?')) return;
    try {
      await deleteCatch(id);
      load(page, selectedClubId);
    } catch {
      setError('Failed to delete catch.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showBadge = selectedClub != null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Catches</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {clubs.length > 0 && (
            <select
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              value={selectedClubId ?? ''}
              onChange={e => handleClubChange(e.target.value)}
            >
              <option value="">My catches</option>
              {clubs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <div className="flex gap-1 rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-white dark:bg-slate-800">
            <button
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                view === 'list'
                  ? 'bg-teal-600 dark:bg-teal-500 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                view === 'map'
                  ? 'bg-teal-600 dark:bg-teal-500 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
              onClick={() => setView('map')}
            >
              Map
            </button>
          </div>
          <button
            className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 text-sm font-medium transition-colors cursor-pointer"
            onClick={() => navigate('/catches/log')}
          >
            Log a catch
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-slate-500 dark:text-slate-400">No catches yet. Log your first one!</p>
      ) : view === 'list' ? (
        <CatchTable
          rows={items.map(fromCatchResponse)}
          columns={{ spot: true, angler: showBadge }}
          renderActions={(row) => (
            <button
              className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-400 cursor-pointer transition-colors"
              onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            >
              Delete
            </button>
          )}
        />
      ) : (
        <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm">
          <CatchesMap
            catches={items}
            height="500px"
            onCatchClick={c => navigate(`/catches/${c.id}`)}
            clubName={selectedClub?.name ?? null}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 items-center">
          <button
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors cursor-pointer"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Prev
          </button>
          <span className="text-sm text-slate-500 dark:text-slate-400">Page {page} of {totalPages}</span>
          <button
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors cursor-pointer"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
